/**
 * 작성자 : 김광민
 * 날짜 : 2024.01.25
 * 경로 : 구역별 상황판 > 유해가스 종합현황 (원형, 수치형)
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import { useTranslation } from 'react-i18next';
import { PulseLoader } from 'react-spinners';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SensorTable from './MGasInfoWithTatCd/SensorTable';
import { arraySortByAscdOrder } from '../../utils/arraySortByAscdOrder';
import IssueGuide from '../IssueGuide';
import { ERROR } from '../../_constants';
import { ISensorRange } from 'customTypes';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  user-select: none;
  > .title {
    height: 2rem;
    width: 100%;
    padding: 0.75rem 1rem 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 1;
    .name {
      color: var(--text-primary);
      font-weight: var(--font-semibold);
      font-size: 1.125rem;
      line-height: 1.2;
    }
    .legendGroup {
      display: flex;
      gap: 0.25rem;
      .legend {
        padding: 0 0.375rem;
        border-radius: 0.25rem;
        font-size: 0.875rem;
      }
    }
  }
  .blue {
    color: var(--blue-500);
    background-color: var(--tonal-blue);
  }
  .red {
    color: var(--filled-red);
    background-color: var(--tonal-red);
  }
  .violet {
    color: var(--filled-violet);
    background-color: var(--tonal-violet);
  }
  .orange {
    color: var(--filled-orange);
    background-color: var(--tonal-orange);
  }
  .green {
    color: var(--filled-green);
    background-color: var(--tonal-green);
  }
  .amber {
    color: var(--filled-amber);
    background-color: var(--tonal-amber);
  }
  .slate {
    color: var(--filled-slate);
    background-color: var(--tonal);
  }
`;

type Props = {
  name: string;
  userInfo: any;
  valueType?: 'shape' | 'number';
  tatCd: string;
  refetchInterval: number;
};

const MGasInfoWithTatCd = ({ name, userInfo, valueType = 'shape', tatCd, refetchInterval }: Props) => {
  const { t } = useTranslation();

  const tatCdParams = { hCd: userInfo.hCd, sCd: userInfo.sCd, tatCd };
  const sensorUlistParams = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd: '08' };
  const [sensorList, setSensorList] = useState<string[]>([]);

  const request = {
    tareaList: { queryKey: `tareaSensorGetInArea_${valueType}`, endPoint: '/tarea/t/sensor', params: tatCdParams },
    sensorList: { queryKey: `sensorUListGetInArea_${valueType}`, endPoint: '/sensor/ulist', params: sensorUlistParams },
  };

  const fetchData = (url: string, req: any) => () => apiGet({ path: url, req }).then((res) => res.data.data);

  const sensorQueryKey = [request.sensorList.queryKey, userInfo.hCd, userInfo.sCd];
  const tareaQueryKey = [request.tareaList.queryKey, userInfo.hCd, userInfo.sCd];

  const {
    data: sensorUlistData,
    isLoading: isLoadingSensorUlistData,
    isError: isErrorSensorUlistData,
  } = useQuery(sensorQueryKey, fetchData(request.sensorList.endPoint, request.sensorList.params), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!tatCd,
    refetchInterval,
    staleTime: 30000,
  });

  const tareaQuery = useQuery(tareaQueryKey, fetchData(request.tareaList.endPoint, request.tareaList.params), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!tatCd,
    refetchInterval,
    staleTime: 30000,
  });

  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } } = {};
      res.data.data.rangeList.forEach((list: ISensorRange) => {
        rangeMap[list.srCd] = {
          level1: list.level1 ? Number(list.level1) : 0,
          level2: list.level2 ? Number(list.level2) : 0,
          level3: list.level3 ? Number(list.level3) : 0,
          unit: list.unit,
        };
      });
      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    isFetching: isFetchingAllSensorRanges,
  } = useQuery([`allSensorRangesGetInArea_${valueType}`, userInfo.hCd, userInfo.sCd], () => fetchAllSensorRanges(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!tatCd,
    refetchInterval,
    staleTime: 30000,
    cacheTime: 0,
  });

  useEffect(() => {
    if (sensorUlistData) {
      const { sensorUList } = sensorUlistData;
      const useYList = sensorUList.filter((el: any) => el.useYn === 'Y' && el.uListYn === 'Y');
      setSensorList(arraySortByAscdOrder(useYList, 'etc1'));
    }
  }, [sensorUlistData]);

  const isLoading = isLoadingSensorUlistData || tareaQuery.isLoading || isLoadingAllSensorRanges;
  const isError = isErrorSensorUlistData || tareaQuery.isError;

  if (isLoading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  if (isError) return <IssueGuide type='error' text={ERROR} />;

  return (
    <Root>
      <div className='title'>
        <span className='name'>{t('유해가스 현황')}</span>
        <div className='legendGroup'>
          <span className='legend blue'>{t('좋음')}</span>
          <span className='legend green'>{t('주의')}</span>
          <span className='legend amber'>{t('경고')}</span>
          <span className='legend red'>{t('위험')}</span>
        </div>
      </div>
      <SensorTable sensorList={sensorList} tareaData={tareaQuery?.data?.tareaList} valueType={valueType} allSensorRanges={allSensorRanges} />
    </Root>
  );
};

export default React.memo(MGasInfoWithTatCd);
